import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Copy, Instagram, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TUC_ROUNDS } from "@/components/NextRace";

/**
 * Admin page that generates an Instagram-ready 1080×1350 post for any trail
 * + a pre-baked caption with hashtags. One click → download → post.
 *
 * Pattern: pick trail → preview image → "Download" + "Copy caption" → post on IG.
 */
export default function AdminSocialPost() {
    const { user, checking } = useAuth();
    const nav = useNavigate();
    const [trails, setTrails] = useState([]);
    const [products, setProducts] = useState([]);
    const [reports, setReports] = useState({});
    const [selected, setSelected] = useState(null);
    const [lang, setLang] = useState("it");
    const [caption, setCaption] = useState("");
    const [loadingCaption, setLoadingCaption] = useState(false);
    const [bust, setBust] = useState(0);

    useEffect(() => {
        if (!checking && (!user || user.role !== "admin")) nav("/login");
    }, [user, checking, nav]);

    useEffect(() => {
        api.get("/shop/catalog").then((r) => setProducts(r.data || [])).catch(() => {});
        api.get("/trails").then((r) => {
            const ts = (r.data || []).sort((a, b) => a.name.localeCompare(b.name));
            TUC_ROUNDS.slice().reverse().forEach((x) => ts.unshift({ slug: `event-tuc-${x.n}`, name: `★ TUC R${x.n} · ${x.label} · ${x.place}` }));
            ts.unshift({ slug: "event-tes-course", name: "★ IL GIRO È FUORI · TES giro gara ufficiale" });
            ts.unshift({ slug: "event-tes", name: "★ EVENTO · Toscano Enduro Series · 04.10.2026" });
            setTrails(ts);
            if (ts.length && !selected) setSelected(ts[0].slug);
        });
        api.get("/trail-reports/latest-all").then((r) => setReports(r.data || {}));
    }, []);

    useEffect(() => {
        if (!selected) return;
        setLoadingCaption(true);
        api.get(`/admin/social-post/${selected}/caption`, { params: { lang } })
            .then((r) => setCaption(r.data?.caption || ""))
            .catch(() => setCaption(""))
            .finally(() => setLoadingCaption(false));
    }, [selected, lang, bust]);

    const trail = useMemo(() => trails.find((t) => t.slug === selected), [trails, selected]);
    const report = reports[selected];

    const imageUrl = useMemo(() => {
        if (!selected) return "";
        // The cookie auth carries through because admin/* endpoints check the
        // same JWT cookie; we still cache-bust with `bust` for refresh.
        return `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/social-post/${selected}/image?lang=${lang}&v=${bust}`;
    }, [selected, lang, bust]);

    const isEvent = String(selected).startsWith("event-tes");
    const storyUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/social-post/${selected}/story?lang=${lang}&v=${bust}`;

    const downloadImage = async (kind = "image") => {
        try {
            const r = await api.get(`/admin/social-post/${selected}/${kind}`, {
                params: { lang },
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `abnormal-${selected}${kind === "story" ? "-story" : ""}.jpg`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Immagine scaricata");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore download");
        }
    };

    const copyCaption = async () => {
        try {
            await navigator.clipboard.writeText(caption);
            toast.success("Caption copiata");
        } catch (e) {
            window.prompt("Copia la caption:", caption);
        }
    };

    if (checking) return <div className="p-20 text-zinc-500">…</div>;
    if (!user || user.role !== "admin") return null;

    return (
        <div data-testid="admin-social-post-page" className="max-w-[1400px] mx-auto px-5 md:px-10 py-14">
            <Link to="/admin" className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-abnormal-red inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Admin
            </Link>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
                <Instagram className="text-abnormal-red" />
                <h1 className="font-display text-4xl md:text-6xl uppercase leading-none">Social Post</h1>
            </div>
            <p className="text-zinc-400 mt-4 max-w-2xl text-sm">
                Genera in 1 secondo un&apos;immagine 1080×1350 pronta per Instagram + caption con hashtag. Scarica, incolla su IG, fatto.
            </p>

            <div className="grid lg:grid-cols-[1fr_1fr] gap-8 mt-10">
                {/* LEFT: controls */}
                <div className="space-y-6">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-400 block mb-2">Trail</label>
                        <select
                            data-testid="social-trail-select"
                            value={selected || ""}
                            onChange={(e) => setSelected(e.target.value)}
                            className="w-full bg-zinc-950 border border-white/15 text-white px-4 py-3 font-bold uppercase tracking-wider"
                        >
                            {trails.map((t) => (
                                <option key={t.slug} value={t.slug}>
                                    {t.name}{reports[t.slug] ? "  · ✓ report" : "  · no report"}
                                </option>
                            ))}
                            {products.length > 0 && (
                                <optgroup label="◆ SHOP · prodotti Shopify" data-testid="social-products-group">
                                    {products.map((p) => (
                                        <option key={p.handle} value={`product-${p.handle}`}>◆ {p.title} · {p.priceRange?.minVariantPrice?.amount} €</option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-400 block mb-2">Lingua</label>
                        <div className="flex gap-2">
                            {["it", "en"].map((l) => (
                                <button
                                    key={l}
                                    data-testid={`social-lang-${l}`}
                                    onClick={() => setLang(l)}
                                    className={`px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold border ${
                                        lang === l ? "bg-abnormal-red text-white border-abnormal-red" : "border-white/15 text-zinc-300 hover:border-white"
                                    }`}
                                >
                                    {l}
                                </button>
                            ))}
                            <button
                                onClick={() => setBust((x) => x + 1)}
                                data-testid="social-refresh"
                                className="ml-auto px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold border border-white/15 text-zinc-300 hover:border-white inline-flex items-center gap-2"
                            >
                                <RefreshCw size={12} /> Aggiorna
                            </button>
                        </div>
                    </div>

                    {trail && (
                        <div className="border border-white/10 p-4 text-sm">
                            <p className="text-xs uppercase tracking-[0.25em] font-bold text-abnormal-red mb-2">Report attuale</p>
                            {report ? (
                                <div className="text-zinc-300 space-y-1">
                                    <p><span className="text-zinc-500">Condizione:</span> <b>{report.condition}</b> · <b>{report.rating}/5</b></p>
                                    {report.comment && <p className="text-zinc-400 italic">&ldquo;{report.comment}&rdquo;</p>}
                                    <p className="text-[10px] text-zinc-500">— @{report.user_name}</p>
                                </div>
                            ) : (
                                <p className="text-zinc-500 italic">Nessun report attivo per questo trail. Il post mostrerà solo il nome.</p>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-400 block mb-2">Caption (pronta da incollare)</label>
                        <textarea
                            data-testid="social-caption"
                            value={caption}
                            readOnly
                            rows={11}
                            className="w-full bg-zinc-950 border border-white/15 text-zinc-200 px-4 py-3 text-sm font-mono leading-relaxed"
                        />
                        <div className="flex gap-3 mt-3">
                            <button
                                onClick={copyCaption}
                                disabled={loadingCaption || !caption}
                                data-testid="social-copy-caption"
                                className="flex-1 bg-white text-black font-bold uppercase tracking-[0.25em] text-xs px-5 py-3 hover:bg-abnormal-red hover:text-white transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {loadingCaption ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
                                Copia caption
                            </button>
                            <button
                                onClick={() => downloadImage("image")}
                                data-testid="social-download"
                                className="flex-1 bg-abnormal-red text-white font-bold uppercase tracking-[0.25em] text-xs px-5 py-3 hover:bg-white hover:text-abnormal-red border-2 border-abnormal-red transition-colors inline-flex items-center justify-center gap-2"
                            >
                                <Download size={14} /> Scarica immagine
                            </button>
                        </div>
                    </div>
                </div>

                {/* RIGHT: live preview */}
                <div className="space-y-8">
                    <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-400 block mb-2">Anteprima · 1080×1350</p>
                    <div className="aspect-[4/5] bg-zinc-950 border border-white/15 overflow-hidden flex items-center justify-center">
                        {selected && (
                            <img
                                key={`${selected}-${lang}-${bust}`}
                                src={imageUrl}
                                alt={trail?.name || "Anteprima social"}
                                data-testid="social-preview-img"
                                className="w-full h-full object-contain bg-black"
                            />
                        )}
                    </div>
                    </div>
                    {isEvent && (
                        <div data-testid="social-story-block">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-400">Storia · 1080×1920 · countdown live</p>
                                <button onClick={() => downloadImage("story")} data-testid="social-download-story"
                                    className="inline-flex items-center gap-2 label-mono text-abnormal-acid hover:text-white">
                                    <Download size={13} /> Scarica storia
                                </button>
                            </div>
                            <div className="aspect-[9/16] max-h-[640px] mx-auto bg-zinc-950 border border-white/15 overflow-hidden flex items-center justify-center">
                                <img key={`story-${lang}-${bust}`} src={storyUrl} alt="Anteprima storia" data-testid="social-story-img" className="w-full h-full object-contain bg-black" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
